/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useCallback, useMemo } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import AiSearchIcon from '@atlaskit/icon-lab/core/ai-search';
import TableIcon from '@atlaskit/icon/core/table';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import Tile from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';
import { KnowledgeDiscoveryJiraIntentDetectionModel } from '@atlassian/search-client/knowledge-discovery/types';
import { AllAppsIcon } from '@atlassian/search-common/all-apps-icon';
import { AllAppsIconWithGithub } from '@atlassian/search-common/all-apps-icon-with-github';
import { addRecentSearch, RECENT_SEARCH_KEY } from '@atlassian/search-common/recent-searches';

import {
	GITHUB_ICON_EXPERIMENT_DEFAULT,
	GITHUB_ICON_EXPERIMENT_NAME,
	GITHUB_ICON_EXPERIMENT_PARAM,
} from '../../../../common/constants/experiments';
import { ReturnIcon } from '../../../../common/constants/icons/return';
import { FlipList } from '../../../../common/ui/flip-list';
import { SearchResult } from '../../../../common/ui/quick-find/search-dialog-result';
import { sha256Hash } from '../../../../common/utils/events/utils';
import { serveJiraNINFpsNLQNavigation } from '../../../../common/utils/experiments/experiment-exports';
import { INTENT_DETECTION, IntentMode } from '../../../../common/utils/intent-detection';
import { onAdvancedSearchLinkSelected } from '../../../../common/utils/quick-find/events/advanced-search-link-selected';
import { onIntentRouterSelected } from '../../../../common/utils/quick-find/events/intent-router-selected';
import { getWordCount } from '../../../../controllers/quick-find/analytics';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../../controllers/quick-find/analytics';
import { useIntentDetectionVitalStats } from '../../../../controllers/quick-find/use-intent-detection-vitalstats';
import { isOpenInNewTabInteraction } from '../../../../controllers/quick-find/utils';
import {
	usePrimaryProduct,
	useSearchSessionId,
	useUser,
} from '../../../../controllers/store/bootstrap';
import {
	useQuickFindActions,
	useUpdateRecentQueries,
} from '../../../../controllers/store/quick-find';
import { useSearchActions } from '../../../../controllers/store/search';

import IntentRouterWorkItem from './intent-router-work-item';
import { messages } from './messages';
import { useIntentDetectionService } from './use-intent-detection-service';
import { useIntentRouterKeyboardPreselection } from './use-intent-router-keyboard-preselection';
import { useIntentRoutes } from './use-intent-routes';
import { useIntentRouting } from './use-intent-routing';
import { modelIdValidator } from './utils';

const styles = cssMap({
	root: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.025'),
	},
	intentRouterQuery: {
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
});

/**
 * Static IntentRouter rows.
 *
 * Returns two destination rows using SearchResult so they integrate
 * with @atlassian/search-dialog keyboard navigation.
 *
 */
export const IntentRouter = ({
	query,
	minQueryLength,
	matchingItem,
	isLoading,
	showMatchingWorkItem,
}: {
	query: string;
	minQueryLength: number;
	matchingItem?: React.JSX.Element;
	isLoading?: boolean;
	showMatchingWorkItem?: boolean;
}): React.JSX.Element => {
	const intl = useIntl();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();
	const [searchSessionId] = useSearchSessionId();
	const [user] = useUser();
	const [primaryProduct] = usePrimaryProduct();
	const updateRecentQueries = useUpdateRecentQueries();
	const { setQuickFindOpen } = useQuickFindActions();
	const { clearAllFilters } = useSearchActions();
	const { isTaskActive } = useIntentDetectionVitalStats();

	// Show the GitHub icon variant in the All Apps row for brand awareness.
	// No GitHub connection check is needed here — this matches the pattern in
	// use-jira-all-apps-icon.tsx where the icon is shown to all Jira users in the experiment.
	const showGitHubIntentIcon = useMemo(
		() =>
			primaryProduct === 'jira' &&
			FeatureGates.getExperimentValue<string>(
				GITHUB_ICON_EXPERIMENT_NAME,
				GITHUB_ICON_EXPERIMENT_PARAM,
				GITHUB_ICON_EXPERIMENT_DEFAULT,
			) === 'true',
		[primaryProduct],
	);

	const intentDetectionExperiment = serveJiraNINFpsNLQNavigation({
		primaryProduct,
		fireLayerExposure: false,
		locale: intl.locale,
	});
	const intentDetectionModelId = intentDetectionExperiment?.modelId;
	const intentDetectionCohort = intentDetectionExperiment?.cohort;

	const modelId = modelIdValidator(intentDetectionModelId)
		? intentDetectionModelId
		: KnowledgeDiscoveryJiraIntentDetectionModel.GNIN_HEURISTIC;

	const allAppsLabel = intl.formatMessage(messages.allApps);
	const jiraWorkItemsLabel = intl.formatMessage(messages.jiraWorkItems);

	const { defaultMode, allAppsUrl, jiraWorkItemsUrl } = useIntentRoutes({
		query,
		minQueryLength,
		showMatchingWorkItem,
		isLoading,
	});

	// Use the initial primary mode as the default mode for intent detection
	const intentDetection = useIntentDetectionService({ defaultMode });

	// Omitting pauseAndResume from the result as it is not needed for intent router.
	// If we need it in the future, we can add it back to the result.
	const { primaryMode, isPaused: disabled } = useIntentRouting({
		query,
		debounceMs: 250,
		intentDetection,
		initialPrimaryMode: defaultMode,
		minQueryLength,
	});

	// Keep keyboard selection in sync with the active intent mode and query.
	useIntentRouterKeyboardPreselection({
		primaryMode,
		query,
		showMatchingWorkItem,
		hasMatchingWorkItem: !!matchingItem,
		isLoadingWorkItem: isLoading,
	});

	const handleResultClicked = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			selectedMode: IntentMode,
			buttonPosition: 0 | 1,
		) => {
			const newTab = isOpenInNewTabInteraction(event);
			if (!newTab) {
				setQuickFindOpen(false);
				// Keep parity with "Enter on input" behaviour: reset QF filters as FPS is the source of truth.
				clearAllFilters();
			}

			const trimmedQuery = query.trim();
			if (trimmedQuery !== '' && user && user.id && primaryProduct) {
				addRecentSearch({
					value: fg('rovo_qf_remember_last_intent_for_recents')
						? {
								query: trimmedQuery,
								metadata: {
									intentMode: selectedMode,
								},
							}
						: trimmedQuery,
					userId: user.id,
					productId: primaryProduct,
					key: RECENT_SEARCH_KEY,
				});
				updateRecentQueries?.();
			}

			const queryHash = sha256Hash(query);
			const wordCount = getWordCount(query);

			fireAnalyticsEvent(
				onIntentRouterSelected({
					attributes: {
						searchSessionId: searchSessionId || '',
						selectedMode,
						queryClassification: primaryMode,
						queryHash,
						buttonPosition,
						queryLength: query.length,
						wordCount,
						networkResponded: !isTaskActive(),
						modelId,
						cohort: intentDetectionCohort,
					},
				}),
			);

			fireAnalyticsEvent(
				onAdvancedSearchLinkSelected({
					actionSubjectId: 'jiraAdvancedSearchLink',
					attributes: {
						...commonAttributes,
						newTab,
						isLoading: isTaskActive(),
						selectedMode,
						buttonPosition,
						queryClassification: primaryMode,
						modelId,
						wordCount,
						cohort: intentDetectionCohort,
					},
					nonPrivacySafeAttributes,
				}),
			);
		},
		[
			clearAllFilters,
			commonAttributes,
			fireAnalyticsEvent,
			modelId,
			nonPrivacySafeAttributes,
			primaryMode,
			primaryProduct,
			query,
			searchSessionId,
			setQuickFindOpen,
			updateRecentQueries,
			user,
			isTaskActive,
			intentDetectionCohort,
		],
	);

	// Update the global intent detection result
	INTENT_DETECTION.update({
		mode: primaryMode,
		route: primaryMode === IntentMode.FPS ? allAppsUrl : jiraWorkItemsUrl,
		query, // store the query to aid hydration
		modelId,
	});

	const orderedModes: IntentMode[] =
		primaryMode === IntentMode.GNIN
			? [IntentMode.GNIN, IntentMode.FPS]
			: [IntentMode.FPS, IntentMode.GNIN];

	const rowsByMode: Record<IntentMode, React.JSX.Element> = {
		[IntentMode.FPS]: (
			<SearchResult
				key="intent-all-apps"
				id="intent-all-apps"
				resultType="intentRouter"
				icon={
					<Tile label="" size="small">
						<AiSearchIcon label={allAppsLabel} />
					</Tile>
				}
				title={
					<Flex direction="row" alignItems="center" gap="space.100">
						<Box xcss={styles.intentRouterQuery}>
							{query || intl.formatMessage(messages.searchForAllApps)}
						</Box>
						<Text size="small" color="color.text.subtlest">
							––
						</Text>
						<Text size="small" color="color.text.subtlest">
							{intl.formatMessage(messages.allApps)}
						</Text>
						{showGitHubIntentIcon ? <AllAppsIconWithGithub /> : <AllAppsIcon />}
					</Flex>
				}
				url={allAppsUrl}
				onResultClicked={(event) =>
					handleResultClicked(event, IntentMode.FPS, primaryMode === IntentMode.FPS ? 0 : 1)
				}
				highlightedRightMetadata={<ReturnIcon />}
			/>
		),
		[IntentMode.GNIN]: (
			<SearchResult
				key="intent-jira-work-items"
				id="intent-jira-work-items"
				resultType="intentRouter"
				icon={
					<Tile label="" size="small">
						<TableIcon label={jiraWorkItemsLabel} />
					</Tile>
				}
				title={
					<Flex direction="row" gap="space.100">
						<Box xcss={styles.intentRouterQuery}>
							{query || intl.formatMessage(messages.searchForJiraWorkItems)}
						</Box>
						<Text size="small" color="color.text.subtlest">
							––
						</Text>
						<Text size="small" color="color.text.subtlest">
							{intl.formatMessage(messages.jiraWorkItems)}
						</Text>
					</Flex>
				}
				url={jiraWorkItemsUrl}
				onResultClicked={(event) =>
					handleResultClicked(event, IntentMode.GNIN, primaryMode === IntentMode.GNIN ? 0 : 1)
				}
				highlightedRightMetadata={<ReturnIcon />}
			/>
		),
	};

	const order = orderedModes.map((mode) =>
		mode === IntentMode.FPS ? 'intent-all-apps' : 'intent-jira-work-items',
	);

	return (
		<>
			{showMatchingWorkItem && (
				<IntentRouterWorkItem matchingItem={matchingItem} query={query} isLoading={isLoading} />
			)}
			<FlipList
				order={order}
				disabled={disabled}
				containerAs="div"
				containerProps={{ role: 'list' }}
				itemAs="div"
				renderItem={(key) => {
					return key === 'intent-all-apps'
						? rowsByMode[IntentMode.FPS]
						: rowsByMode[IntentMode.GNIN];
				}}
				xcss={styles.root}
			/>
		</>
	);
};
